/**
 * The Skryptos Score: how well a contract suits BASE v1's way of turning
 * capital over.
 *
 * A product signature rather than a table cell, and the two things it must
 * never be mistaken for are a price prediction and a buy signal. So it is shown
 * on its own 0-100 scale, as an integer, and the rail beneath it uses the
 * accent rather than the colour scale that means money on every other screen —
 * a high score is a good fit, not a profit.
 *
 * The rail is the point. A reader scanning thirty rows sees shape before
 * digits, and a row whose rail is a third full is refused faster than one whose
 * number has to be read. It carries no information the number does not, which
 * is exactly why it is safe to read instead of the number.
 *
 * The rank is not here. Where a score sits in a ranked list the rank has its
 * own column, and repeating it inside the score says the same thing twice and
 * invites the reader to check whether the two agree.
 */

export const SCORE_DESCRIPTION =
  "Оценка пригодности контракта для оборачиваемости BASE v1";

type Props = {
  readonly value: number;
  readonly testId?: string;
};

export function Score({ value, testId }: Props): React.JSX.Element {
  // Clamped for the rail only. A score outside the scale is a defect upstream,
  // and a rail wider than its track would hide it by overflowing silently.
  const filled = Math.max(0, Math.min(100, value));

  return (
    // A fixed 2.5rem track with the figure right-aligned above it. Sized to
    // the widest score there can be — three digits — so a row scoring 100 and
    // a row scoring 94 put their rails in the same place. Sized to the number
    // instead, the rails stepped left and right down the column and the eye
    // read that as movement in the data.
    <span
      className="flex w-10 shrink-0 flex-col items-end gap-1.5"
      title={SCORE_DESCRIPTION}
      aria-label={`${String(value)} из 100. ${SCORE_DESCRIPTION}`}
    >
      <span
        data-testid={testId}
        className="font-numeric text-body-main tabular-nums text-text-primary"
      >
        {value}
      </span>
      <span aria-hidden="true" className="h-[3px] w-full rounded-row bg-surface-selected">
        <span
          className="block h-full rounded-row bg-action-primary"
          style={{ width: `${String(filled)}%` }}
        />
      </span>
    </span>
  );
}
